import * as RadioGroup from '@radix-ui/react-radio-group';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useState } from 'react';
import { SurveyRadioItem } from './survey-radio-item';
import { Input } from '~/components/ui/Input';
import { classNames } from '~/utils/classNames';

export interface Data {
  usecase?: Usecase;
  usecase_note?: string;
}

type Usecase = 'myself' | 'launching-business' | 'team-or-company' | 'clients-or-customers' | 'school' | 'other';

const USECASES: Array<{ key: Usecase; value: string; note?: string; icon: string }> = [
  { key: 'myself', value: 'Myself', note: '(Personal/Hobby)', icon: 'i-heroicons:user' },
  { key: 'launching-business', value: 'Launching Business', icon: 'i-heroicons:rocket-launch' },
  { key: 'team-or-company', value: 'Team or Company', icon: 'i-heroicons:building-office-2' },
  { key: 'clients-or-customers', value: 'Clients or Customers', icon: 'i-heroicons:briefcase' },
  { key: 'school', value: 'School', icon: 'i-heroicons:academic-cap' },
  { key: 'other', value: 'Other', icon: 'i-heroicons:question-mark-circle' },
];

interface Props {
  initialValues?: Data;
  className?: string;
  validate: () => void;
}

const SurveyStep4 = forwardRef<HTMLDivElement, Props>(({ initialValues, className, validate }, ref) => {
  const [usecase, setUsecase] = useState(typeof initialValues?.usecase === 'string' ? initialValues?.usecase : '');

  return (
    <div className={className} ref={ref}>
      <h2 className="text-[32px]/[39px] font-semibold max-w-[400px]">Who are you primarily building products for?</h2>

      <RadioGroup.Root
        defaultValue={usecase}
        name="usecase"
        aria-label="Use case"
        onValueChange={setUsecase}
        className="mt-8 grid grid-cols-3 justify-center gap-3"
        required
      >
        {USECASES.map((usecase) => (
          <SurveyRadioItem
            key={usecase.key}
            value={usecase.key}
            id={`usecase-${usecase.key}`}
            className="aspect-square flex flex-col items-center justify-center"
          >
            <span className={classNames('size-12 mb-4', usecase.icon)} />
            <div className="min-h-10 text-sm grid content-center">
              <div className="font-semibold">{usecase.value}</div>
              {usecase.note && <div className="text-bolt-ds-textSecondary">{usecase.note}</div>}
            </div>
          </SurveyRadioItem>
        ))}
      </RadioGroup.Root>

      <AnimatePresence onExitComplete={validate}>
        {usecase === 'Other' && (
          <motion.div
            className="flex items-center w-full gap-2 mt-4"
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -5 }}
            onAnimationComplete={validate}
          >
            <label htmlFor="usecase_note-input" className="text-bolt-ds-textSecondary text-sm font-medium">
              Please specify
            </label>
            <Input
              type="text"
              name="usecase_note"
              id="usecase_note"
              size="sm"
              placeholder="Describe your usecase..."
              autoFocus
              required
              className="flex-1"
              defaultValue={initialValues?.usecase_note}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SurveyStep4.displayName = 'SurveyStep4';

export default SurveyStep4;
